import {
  Card as MuiCard,
  CardHeader,
  CardContent,
  CardProps,
} from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import { DesignToken } from '../core';

import Spacer, { spc } from '~c/components/base/spacer';
import Text from '~c/components/base/text';

type CustomCardProps = Omit<CardProps, 'title'>;

interface Props extends CustomCardProps {
  className?: string;
  /**
   * The title of card
   */
  title?: React.ReactNode;
  /**
   * Render action component on the top right corner of the card
   */
  action?: React.ReactNode;
  subHeader?: React.ReactNode;
}

const CardImpl = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      title,
      action,
      // Default to outline (to remove elevation)
      variant = 'outlined',
      children,
      subHeader,
      ...props
    },
    ref
  ) => {
    return (
      <MuiCard
        className={className}
        ref={ref}
        sx={{ p: spc.s }}
        variant={variant}
        {...props}
      >
        <CardHeader
          sx={{ p: 0, pb: children && (title || subHeader) ? spc.m : 0 }}
          title={
            typeof title === 'string' ? <Text sz="title">{title}</Text> : title
          }
          subheaderTypographyProps={{
            ...(!!title && { sx: { pt: spc.xxs } }),
          }}
          subheader={
            typeof subHeader === 'string' ? (
              <Text sz="body12" color="secondary">
                {subHeader}
              </Text>
            ) : (
              subHeader
            )
          }
          action={action}
        />
        <CardContent sx={{ p: 0 }}>{children}</CardContent>
      </MuiCard>
    );
  }
);

/**
 * Customized MUI Card component.
 * - custom padding for CardHeader
 * - custom padding for CardContent
 *
 * It inherits all properties of a MUI Card
 */
export const Card = muiStyled(CardImpl)(
  ({ theme }) => css`
    @media print {
      background-image: linear-gradient(
        ${DesignToken.surface1},
        ${DesignToken.surface1}
      );
      .MuiTypography-root {
        color: ${DesignToken.textMuted};
      }
    }
    /* Remove the default bottom padding from MuiCardContent */
    .MuiCardContent-root:last-child {
      padding-bottom: 0;
    }
    ${theme.breakpoints.down('sm')} {
      .MuiCardHeader-root {
        flex-wrap: wrap;
      }
      .MuiCardHeader-root .MuiCardHeader-action {
        margin-top: ${theme.spacing(1)};
        margin-left: 0;
        width: 100%;
      }
    }
  `
);
export default Card;
